import React, { useEffect, useState, useRef } from 'react';
import { getShipments, getShipmentImages, getExpected, createExpected, deleteExpected, syncExpected } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import InsideImagesModal from '../../components/InsideImagesModal';
import LanguageToggle from '../../components/LanguageToggle';
import { useT } from '../../i18n';

const WAREHOUSE_NAMES = {
  'Kho 1': 'Kho 4450 Van',
  'Kho 2': 'Kho 7320 Cal',
  'Kho 3': 'Kho 3838/135 Cal',
  'ARBOURRIDGE': 'ARBOURRIDGE (Cal)',
  'Kho 4': 'Kho 465 Toronto',
  'Kho Surrey': 'Kho Surrey',
};
const warehouseName = (w) => WAREHOUSE_NAMES[w] || w || '—';

// Locations where a shipment can be directly assigned to a flight.
// Kho 2 & Kho 3 must be transferred to a main warehouse first.
const READY_LOCATIONS = ['Kho 1', 'Kho 4', 'Kho Surrey', 'Đã chuyển lên Kho 1'];
const isReadyForFlight = (s) => READY_LOCATIONS.includes(s.current_location || s.warehouse);

function F({ label, value, green, flight }) {
  const t = useT();
  const flightColor = flight === 'flown' ? 'var(--accent)' : 'var(--color-warning)';
  return (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1.2,marginBottom:4}}>{label}</div>
      {flight ? (
        <div style={{fontSize:13,fontWeight:700,color:flightColor,fontVariantNumeric:"tabular-nums",fontWeight:600}}>
          ✈ {value} · {flight==='flown'?t('Flown'):t('Pending')}
        </div>
      ) : green && value ? (
        <div style={{fontSize:14,fontWeight:700,color:'var(--accent)',fontVariantNumeric:"tabular-nums",fontWeight:600}}>{value}</div>
      ) : (
        <div style={{fontSize:13,color:value?'var(--text-primary)':'var(--text-muted)',lineHeight:1.4}}>{value||'—'}</div>
      )}
    </div>
  );
}

function MultiResultItem({ r, onImageClick, onInsideClick }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const { code, shipment: sh } = r;

  if (!sh) {
    return (
      <div style={{padding:'12px 16px',background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:10,display:'flex',alignItems:'flex-start',gap:12}}>
        <div style={{flex:1}}>
          <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:700,fontSize:13,color:'var(--text-secondary)',marginBottom:4}}>{code}</div>
          <div style={{fontSize:12,color:'#b91c1c',fontWeight:600,marginBottom:3}}>{t('⏳ Not scanned')}</div>
          <div style={{fontSize:11,color:'var(--text-muted)'}}>{t('Please wait up to 48 hours. If still missing after 48h, contact SDC staff for assistance.')}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{background:'var(--bg-secondary)',border:'1px solid rgba(22,163,74,0.15)',borderRadius:10,overflow:'hidden'}}>
      {/* Summary row - always visible */}
      <div onClick={()=>setExpanded(e=>!e)} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer'}}>
        {sh.image_url && (
          <img src={sh.image_url} alt="label" onClick={e=>{e.stopPropagation();onImageClick(sh.image_url);}}
            style={{width:44,height:44,objectFit:'cover',borderRadius:6,border:'1px solid rgba(22,163,74,0.30)',flexShrink:0}} />
        )}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:2}}>
            <span style={{fontVariantNumeric:"tabular-nums",fontWeight:700,fontSize:13,color:'var(--accent)'}}>{code}</span>
            {sh.flight_name && (
              <span style={{
                fontSize:10,fontWeight:700,
                color: sh.flight_status==='flown' ? 'var(--accent)' : 'var(--color-warning)',
                background: sh.flight_status==='flown' ? 'var(--accent-bg)' : 'rgba(217,119,6,0.10)',
                border: `1px solid ${sh.flight_status==='flown' ? 'rgba(22,163,74,0.22)' : 'rgba(217,119,6,0.20)'}`,
                padding:'2px 8px',borderRadius:20,whiteSpace:'nowrap'
              }}>
                ✈ {sh.flight_name}
              </span>
            )}
          </div>
          <div style={{fontSize:12,color:'var(--text-secondary)'}}>{sh.receiver_name} · {warehouseName(sh.warehouse)}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
          <span style={{fontSize:10,fontWeight:700,color:'var(--accent)',background:'var(--accent-bg)',border:'1px solid rgba(22,163,74,0.22)',padding:'3px 8px',borderRadius:20}}>{t('✅ Arrived')}</span>
          {sh.expected_matched
            ? <span style={{fontSize:10,fontWeight:700,color:'var(--color-info)',background:'rgba(37,99,235,0.10)',border:'1px solid rgba(37,99,235,0.20)',padding:'3px 8px',borderRadius:20}}>{t('🔗 Registered')}</span>
            : <span style={{fontSize:10,fontWeight:700,color:'var(--color-warning)',background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.20)',padding:'3px 8px',borderRadius:20}}>{t('⚠ Not registered')}</span>
          }
          <span style={{color:'var(--text-muted)',fontSize:16}}>{expanded?'▲':'▼'}</span>
        </div>
      </div>

      {/* Detail - expanded */}
      {expanded && (
        <div style={{borderTop:'1px solid rgba(15,23,42,0.06)',padding:'12px 16px',display:'flex',gap:12}}>
          <div style={{flex:1,display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 16px'}}>
            <div><div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>{t('Tracking')}</div><div style={{fontSize:12,color:'var(--text-primary)',fontVariantNumeric:'tabular-nums'}}>{sh.tracking_code||'—'}</div></div>
            <div><div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>{t('Weight')}</div><div style={{fontSize:12,fontWeight:700,color:'var(--accent)'}}>{sh.weight_kg?`${sh.weight_kg} KG`:'—'}</div></div>
            <div><div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>{t('Warehouse')}</div><div style={{fontSize:12,color:'var(--text-primary)'}}>{warehouseName(sh.warehouse)}</div></div>
            <div><div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>{t('Flights')}</div><div style={{fontSize:12,color:sh.flight_name?'var(--color-warning)':'var(--text-muted)'}}>{sh.flight_name||t('Not assigned')}</div></div>
            {sh.can_count != null && (
              <div style={{gridColumn:'1 / -1'}}>
                <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>
                  {sh.can_count_status === 'manual' ? t('Count (staff)') : t('Count (AI)')}
                </div>
                <div style={{fontSize:13,fontWeight:700,color:sh.can_count_status==='manual'?'var(--color-info)':'var(--color-purple)'}}>
                  {sh.can_count}
                </div>
              </div>
            )}
          </div>
          {!sh.expected_matched && (
            <div style={{gridColumn:'1 / -1',marginTop:4,padding:'8px 10px',background:'rgba(217,119,6,0.08)',border:'1px solid rgba(217,119,6,0.20)',borderRadius:8,fontSize:11,color:'#a16207',lineHeight:1.5}}>
              {t('⚠ Shipment not registered. Please go to the 📋 REGISTER CODES tab and register this tracking code so the warehouse can confirm it as yours.')}
            </div>
          )}
          <button onClick={()=>onInsideClick(sh.id,sh.tracking_code)}
            style={{background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.22)',borderRadius:6,padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--color-warning)',cursor:'pointer',alignSelf:'flex-start',whiteSpace:'nowrap'}}>
            {t('📦 Inside')}
          </button>
        </div>
      )}
    </div>
  );
}

function CanCountInfo({ sh }) {
  const t = useT();
  let breakdown = null;
  if (sh.can_count_notes) {
    try { breakdown = JSON.parse(sh.can_count_notes).breakdown; } catch {}
  }
  const isManual = sh.can_count_status === 'manual';
  return (
    <div style={{marginBottom:12,gridColumn:'1 / -1'}}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1.2,marginBottom:4}}>
        {isManual ? t('Count (staff)') : t('Count (AI)')}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:18,fontWeight:700,color:isManual?'var(--color-info)':'var(--color-purple)'}}>
          {sh.can_count}
        </span>
        {breakdown?.length > 0 && (
          <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
            {breakdown.map((b,i)=>(
              <span key={i} style={{fontSize:10,padding:'2px 6px',background:'rgba(124,58,237,0.08)',border:'1px solid rgba(124,58,237,0.18)',borderRadius:10,color:'var(--text-secondary)'}}>
                {b.label}: <strong style={{color:'var(--color-purple)'}}>{b.count}</strong>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Bucket button for Shipments tab — pick one of three groups
function BucketButton({ color, icon, label, sublabel, count, active, onClick }) {
  const t = useT();
  return (
    <div onClick={onClick}
      style={{display:'flex',alignItems:'center',gap:14,padding:'14px 18px',marginBottom:10,cursor:'pointer',
        background:active?`${color}12`:'var(--bg-tertiary)',
        border:`1px solid ${active?`${color}55`:'var(--border-strong)'}`,
        borderRadius:12,transition:'all 0.18s',
        boxShadow:active?`0 0 0 1px ${color}30 inset`:'none'}}
      onMouseEnter={e=>{ if(!active) e.currentTarget.style.borderColor=`${color}30`; }}
      onMouseLeave={e=>{ if(!active) e.currentTarget.style.borderColor='var(--border-strong)'; }}>
      <div style={{width:38,height:38,borderRadius:10,background:`${color}15`,border:`1px solid ${color}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,color,flexShrink:0}}>{icon}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:14,fontWeight:700,color:active?'var(--text-primary)':'var(--text-primary)',marginBottom:2}}>{label}</div>
        <div style={{fontSize:11,color:'var(--text-muted)'}}>{sublabel}</div>
      </div>
      <span style={{fontSize:12,fontWeight:700,color,background:`${color}18`,border:`1px solid ${color}35`,padding:'4px 11px',borderRadius:20,flexShrink:0,fontVariantNumeric:"tabular-nums",fontWeight:600}}>
        {t('{n} shipments', { n: count })}
      </span>
      <span style={{color:active?color:'var(--text-muted)',fontSize:14,flexShrink:0,transition:'transform 0.18s',transform:active?'rotate(180deg)':'none'}}>▼</span>
    </div>
  );
}

function ShipCard({ sh, onImageClick, onInsideClick }) {
  const t = useT();
  return (
    <div style={cs.card}>
      {/* Header */}
      <div style={cs.cardTop}>
        <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <div style={cs.tracking}>{sh.customer_order || sh.tracking_code}</div>
            {sh.expected_matched
              ? <span style={{fontSize:10,fontWeight:700,color:'var(--color-info)',background:'rgba(37,99,235,0.10)',border:'1px solid rgba(37,99,235,0.22)',padding:'3px 8px',borderRadius:20,whiteSpace:'nowrap'}}>{t('🔗 Registered')}</span>
              : <span style={{fontSize:10,fontWeight:700,color:'var(--color-warning)',background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.22)',padding:'3px 8px',borderRadius:20,whiteSpace:'nowrap'}}>{t('⚠ Not registered')}</span>
            }
          </div>
        </div>
        <div style={cs.date}>{new Date(sh.scanned_at).toLocaleString('vi-VN')}</div>
      </div>

      {/* Body: info + image */}
      <div style={cs.cardBody}>
        <div style={cs.infoGrid}>
          <F label={t('Tracking')} value={sh.tracking_code} />
          <F label={t('Recipient')} value={sh.receiver_name} />
          <F label={t('Weight')} value={sh.weight_kg?`${sh.weight_kg} KG`:null} green />
          <F label={t('Warehouse')} value={warehouseName(sh.warehouse)} />
          <F label={t('Flight')} value={sh.flight_name||null} flight={sh.flight_name?sh.flight_status:null} />
          {sh.can_count != null && <CanCountInfo sh={sh} />}
        </div>

        {/* Image */}
        <div style={cs.imageWrap}>
          <div style={cs.imageLabel}>{t('LABEL IMAGE')}</div>
          {sh.image_url ? (
            <img src={sh.image_url} alt="label" onClick={()=>onImageClick(sh.image_url)}
              style={cs.image}
            />
          ) : (
            <div style={cs.imgPlaceholder}>
              <div style={{fontSize:24,opacity:0.2}}>📷</div>
            </div>
          )}
          <button onClick={()=>onInsideClick(sh.id, sh.tracking_code)}
            style={{background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.22)',borderRadius:6,padding:'5px 8px',fontSize:10,fontWeight:600,color:'var(--color-warning)',cursor:'pointer',marginTop:6,width:'100%',textAlign:'center'}}>
            {t('📦 Inside photos')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyShipments() {
  const t = useT();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState(null); // null | 'at-main' | 'transfer' | 'hold' | 'flown'
  const [flownFlight, setFlownFlight] = useState('');
  const [flownOpen, setFlownOpen] = useState(false);
  const flownRef = useRef(null);
  const [imageModal, setImageModal] = useState(null);
  const [insideModal, setInsideModal] = useState(null);
  const [activeTab, setActiveTab] = useState('shipments'); // 'shipments' | 'search' | 'register'

  const toggleBucket = (b) => {
    setBucket(prev => prev === b ? null : b);
    if (b !== 'flown') { setFlownFlight(''); setFlownOpen(false); }
  };

  // Close flown dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (flownRef.current && !flownRef.current.contains(e.target)) setFlownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const [multiInput, setMultiInput] = useState('');
  const [multiResults, setMultiResults] = useState(null); // [{code, shipment|null}]
  const [multiLoading, setMultiLoading] = useState(false);
  const [searchMode, setSearchMode] = useState('exact'); // 'exact' | 'suggest'
  const [suggestQuery, setSuggestQuery] = useState('');
  const [regInput, setRegInput] = useState('');
  const [regWarehouse, setRegWarehouse] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regResults, setRegResults] = useState(null);
  const [expectedList, setExpectedList] = useState([]);
  const [expectedLoading, setExpectedLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      setShipments(await getShipments());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleMultiSearch = async () => {
    const codes = multiInput.trim().split(/\s+/).filter(Boolean);
    if (!codes.length) return;
    setMultiLoading(true);
    try {
      // Search each code
      const results = await Promise.all(codes.map(async code => {
        try {
          const data = await getShipments({ search: code });
          const match = data.find(s =>
            (s.tracking_code && s.tracking_code.toLowerCase() === code.toLowerCase()) ||
            (s.customer_order && s.customer_order.toLowerCase() === code.toLowerCase())
          );
          return { code, shipment: match || null };
        } catch(e) {
          return { code, shipment: null };
        }
      }));
      setMultiResults(results);
    } finally { setMultiLoading(false); }
  };

  const totalKg = shipments.reduce((sum,s)=>sum+(parseFloat(s.weight_kg)||0),0).toFixed(2);

  const loadExpected = async () => {
    setExpectedLoading(true);
    try { setExpectedList(await getExpected()); }
    catch(e) { console.error(e); }
    finally { setExpectedLoading(false); }
  };

  const handleRegister = async () => {
    const codes = regInput.trim().split(/\s+/).filter(Boolean);
    if (!codes.length) return;
    if (!regWarehouse) { alert(t('Please pick a warehouse!')); return; }
    setRegLoading(true);
    try {
      const results = await createExpected({ codes, warehouse: regWarehouse });
      setRegResults(results);
      setRegInput('');
      loadExpected();
    } catch(e) { alert(e.message); }
    finally { setRegLoading(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncExpected();
      setSyncResult(result);
      loadExpected();
    } catch(e) { alert(e.message); }
    finally { setSyncing(false); }
  };

  const handleDeleteExpected = async (id) => {
    await deleteExpected(id);
    loadExpected();
  };

  // 4 fixed buckets:
  // 1. atMainWarehouse — đã ở kho chính, có thể đã gán chuyến hoặc chưa
  //    (gộp 2 trạng thái cũ "Awaiting flight" + "Ready to fly" vì với khách đó vẫn là cùng 1 ý:
  //     "hàng tôi đang nằm ở kho chính của Mỹ, sắp bay").
  // 2. awaitingTransfer — ở Kho 2/3, cần chuyển lên kho chính trước.
  // 3. flown — chuyến đã bay về VN.
  // 4. held — disposition='hold' (khách yêu cầu giữ lại).
  const atMainWarehouse = shipments.filter(s =>
    s.disposition !== 'hold' &&
    s.flight_status !== 'flown' &&
    ((s.flight_id && s.flight_status === 'pending') || (!s.flight_id && isReadyForFlight(s)))
  );
  const awaitingTransfer = shipments.filter(s =>
    !s.flight_id && s.disposition !== 'hold' && !isReadyForFlight(s)
  );
  const onFlightFlown = shipments.filter(s => s.flight_id && s.flight_status === 'flown');
  const heldAtWarehouse = shipments.filter(s => !s.flight_id && s.disposition === 'hold');
  const flownFlightNames = [...new Set(onFlightFlown.map(s => s.flight_name).filter(Boolean))];

  // Items shown for the active bucket (with flight filter if flown)
  let filtered = [];
  if (bucket === 'at-main') filtered = atMainWarehouse;
  else if (bucket === 'transfer') filtered = awaitingTransfer;
  else if (bucket === 'hold') filtered = heldAtWarehouse;
  else if (bucket === 'flown') filtered = flownFlight ? onFlightFlown.filter(s => s.flight_name === flownFlight) : onFlightFlown;
  const filteredKg = filtered.reduce((sum,s) => sum + (parseFloat(s.weight_kg)||0), 0).toFixed(2);
  const filteredCans = filtered.reduce((sum,s) => sum + (s.can_count||0), 0);

  return (
    <div style={s.bg}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing:border-box; }
        body { background:var(--bg-primary); font-family:'DM Sans',sans-serif; margin:0; }
        .fi { background:var(--bg-tertiary); border:1px solid var(--border); border-radius:var(--radius-md); padding:8px 12px; font-size:13px; color:var(--text-primary); font-family:var(--font-sans); outline:none; width:100%; }
        .fi:focus { border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-bg); }
        .fb:hover { background:var(--bg-elevated)!important; border-color:var(--accent)!important; }
        @media (max-width: 600px) {
          .filter-grid { grid-template-columns: 1fr 1fr !important; }
          .card-body-inner { flex-direction: column !important; }
          .image-wrap { width:100% !important; border-left:none !important; border-top:1px solid rgba(15,23,42,0.06) !important; flex-direction:row !important; gap:12px !important; padding:12px 16px !important; align-items:center !important; justify-content:flex-start !important; }
          .info-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <div style={s.topbar}>
        <img src="/logo.png" alt="KDExpress" style={s.logoImg} />
        <div style={s.right}>
          <div style={s.codePill}>{user?.code}</div>
          <LanguageToggle inline />
          <button onClick={()=>{logout();navigate('/login');}} style={s.logoutBtn}>{t('Sign out')}</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div style={s.pageLabel}>{t('MY SHIPMENTS')}</div>
          <div style={s.pageTitle}>{t('Hello, {name}', { name: user?.name })}</div>
        </div>

        {/* Code-in-name tip — đặt dưới tên để mọi tab đều thấy, ưu tiên hiển thị
            vì ảnh hưởng trực tiếp đến tốc độ gán kiện cho khách */}
        {user?.code && /^[A-Z]+$/i.test(user.code) && (
          <div style={{marginBottom:16,padding:'12px 14px',background:'var(--accent-bg)',border:'1px solid rgba(22,163,74,0.25)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
            <div style={{fontWeight:700,color:'var(--accent)',marginBottom:4}}>
              💡 {t('Tip — speed up tracking')}
            </div>
            {t('Add your code ({code}) at the end of the recipient name on each package, separated by a space.', { code: user.code.toUpperCase() })}
            <div style={{marginTop:6}}>
              {t('Example')}: <span style={{fontVariantNumeric:'tabular-nums',fontWeight:700,color:'var(--color-success)'}}>DUNG {user.code.toUpperCase()}</span> ✅
              <span style={{margin:'0 6px',color:'var(--text-muted)'}}>·</span>
              <span style={{fontVariantNumeric:'tabular-nums',fontWeight:700,color:'var(--color-danger)',textDecoration:'line-through'}}>DUNG{user.code.toUpperCase()}</span> ❌
            </div>
          </div>
        )}

        {/* Tab switcher — natural flow: register → view → find */}
        <div style={{display:'flex',gap:0,marginBottom:20,borderRadius:8,overflow:'hidden',border:'1px solid rgba(15,23,42,0.08)'}}>
          <button onClick={()=>{setActiveTab('register');loadExpected();}} style={{flex:1,padding:'10px',fontSize:11,fontWeight:600,border:'none',cursor:'pointer',background:activeTab==='register'?'var(--bg-elevated)':'var(--bg-secondary)',color:activeTab==='register'?'var(--text-primary)':'var(--text-secondary)',borderBottom:activeTab==='register'?'2px solid var(--accent)':'2px solid transparent'}}>
            📋 {t('Register')}
          </button>
          <button onClick={()=>setActiveTab('shipments')} style={{flex:1,padding:'10px',fontSize:11,fontWeight:600,border:'none',cursor:'pointer',background:activeTab==='shipments'?'var(--bg-elevated)':'var(--bg-secondary)',color:activeTab==='shipments'?'var(--text-primary)':'var(--text-secondary)',borderBottom:activeTab==='shipments'?'2px solid var(--accent)':'2px solid transparent'}}>
            📦 {t('Shipments')}
          </button>
          <button onClick={()=>setActiveTab('search')} style={{flex:1,padding:'10px',fontSize:11,fontWeight:600,border:'none',cursor:'pointer',background:activeTab==='search'?'var(--bg-elevated)':'var(--bg-secondary)',color:activeTab==='search'?'var(--text-primary)':'var(--text-secondary)',borderBottom:activeTab==='search'?'2px solid var(--accent)':'2px solid transparent'}}>
            🔍 {t('Find code')}
          </button>
        </div>

        {/* MULTI SEARCH TAB */}
        {activeTab === 'search' && (
          <div>
            {/* Sub-toggle: exact vs suggest */}
            <div style={{display:'flex',gap:6,marginBottom:14}}>
              <button onClick={()=>setSearchMode('exact')} style={{
                flex:1,padding:'8px 6px',fontSize:11,fontWeight:600,cursor:'pointer',borderRadius:6,
                border:`1px solid ${searchMode==='exact'?'rgba(22,163,74,0.35)':'var(--border-strong)'}`,
                background:searchMode==='exact'?'rgba(22,163,74,0.10)':'var(--bg-secondary)',
                color:searchMode==='exact'?'var(--accent)':'var(--text-secondary)'
              }}>🎯 {t('Multiple codes (exact match)')}</button>
              <button onClick={()=>setSearchMode('suggest')} style={{
                flex:1,padding:'8px 6px',fontSize:11,fontWeight:600,cursor:'pointer',borderRadius:6,
                border:`1px solid ${searchMode==='suggest'?'rgba(22,163,74,0.35)':'var(--border-strong)'}`,
                background:searchMode==='suggest'?'rgba(22,163,74,0.10)':'var(--bg-secondary)',
                color:searchMode==='suggest'?'var(--accent)':'var(--text-secondary)'
              }}>💡 {t('Suggest by partial code')}</button>
            </div>

            {searchMode === 'exact' && (<>
              <div style={{marginBottom:12}}>
                <textarea
                  className="fi"
                  rows={3}
                  placeholder={t('Paste multiple codes here, separated by spaces or newlines...')}
                  value={multiInput}
                  onChange={e=>setMultiInput(e.target.value)}
                  style={{resize:'vertical',fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12}}
                />
              </div>
              <button className="fb" style={{...s.filterBtn,marginBottom:20}} onClick={handleMultiSearch} disabled={multiLoading||!multiInput.trim()}>
                {multiLoading ? t('Searching...') : t('🔍 Search')}
              </button>

              {multiResults && (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase',fontWeight:700}}>
                    {t('Results — {got}/{total} codes received', { got: multiResults.filter(r=>r.shipment).length, total: multiResults.length })}
                  </div>
                  {multiResults.map((r, i) => (
                    <MultiResultItem key={i} r={r} onImageClick={setImageModal} onInsideClick={(id,tr)=>setInsideModal({id,tracking:tr})} />
                  ))}
                </div>
              )}
            </>)}

            {searchMode === 'suggest' && (() => {
              const q = suggestQuery.trim().toLowerCase();
              const ready = q.length >= 4;
              const results = ready ? shipments.filter(s =>
                (s.tracking_code && s.tracking_code.toLowerCase().includes(q)) ||
                (s.customer_order && s.customer_order.toLowerCase().includes(q))
              ) : [];
              return (
                <>
                  <div style={{marginBottom:10,padding:'10px 12px',background:'rgba(37,99,235,0.08)',border:'1px solid rgba(37,99,235,0.15)',borderRadius:8,fontSize:11,color:'var(--text-secondary)',lineHeight:1.5}}>
                    {t('Type a partial code (e.g. last 4 digits). The system filters your shipments — useful when you don\'t remember the full code.')}
                  </div>
                  <div style={{position:'relative',marginBottom:14}}>
                    <input
                      className="fi"
                      placeholder={t('Type at least 4 characters...')}
                      value={suggestQuery}
                      onChange={e=>setSuggestQuery(e.target.value)}
                      style={{paddingRight:suggestQuery?32:12,fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:13}}
                    />
                    {suggestQuery && (
                      <button onClick={()=>setSuggestQuery('')}
                        style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
                    )}
                  </div>

                  {q.length === 0 ? null : !ready ? (
                    <div style={{padding:'10px 12px',color:'#a16207',fontSize:12,background:'rgba(217,119,6,0.08)',border:'1px solid rgba(217,119,6,0.15)',borderRadius:8}}>
                      {t('Type {n} more character(s)...', { n: 4 - q.length })}
                    </div>
                  ) : results.length === 0 ? (
                    <div style={{padding:'14px',textAlign:'center',color:'var(--text-secondary)',fontSize:13,background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:8}}>
                      {t('No shipments containing "{q}" in your list', { q: suggestQuery })}
                    </div>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase',fontWeight:700}}>
                        {t('Found {n} shipments containing "{q}"', { n: results.length, q: suggestQuery })}
                      </div>
                      {results.map(sh => (
                        <MultiResultItem key={sh.id} r={{code: sh.customer_order || sh.tracking_code, shipment: sh}}
                          onImageClick={setImageModal}
                          onInsideClick={(id,tr)=>setInsideModal({id,tracking:tr})} />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* REGISTER TAB */}
        {activeTab === 'register' && (
          <div>
            <div style={{marginBottom:16,padding:'12px 14px',background:'rgba(37,99,235,0.08)',border:'1px solid rgba(37,99,235,0.15)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
              {t('Paste tracking codes or customer orders here. Status updates automatically when the warehouse scans.')}
            </div>
            <div style={{marginBottom:8}}>
              <textarea className="fi" rows={4}
                placeholder={t('Paste multiple codes, separated by spaces or newlines...')}
                value={regInput} onChange={e=>setRegInput(e.target.value)}
                style={{resize:'vertical',fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12}}
              />
            </div>
            <div style={{marginBottom:8}}>
              <select className="fi" value={regWarehouse} onChange={e=>setRegWarehouse(e.target.value)}>
                <option value="">{t('-- Pick warehouse --')}</option>
                <option value="Kho 1">Kho 4450 Van</option>
                <option value="Kho 2">Kho 7320 Cal</option>
                <option value="Kho 3">Kho 3838/135 Cal</option>
                <option value="Kho 4">Kho 465 Toronto</option>
                <option value="Kho Surrey">Kho Surrey</option>
                <option value="ARBOURRIDGE">ARBOURRIDGE (Cal)</option>
              </select>
            </div>
            <button className="fb" style={{...s.filterBtn,marginBottom:16}} onClick={handleRegister} disabled={regLoading||!regInput.trim()||!regWarehouse}>
              {regLoading ? t('Saving...') : t('📋 Register')}
            </button>

            {/* Registration result */}
            {regResults && (
              <div style={{marginBottom:20}}>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:8,textTransform:'uppercase',letterSpacing:1,fontWeight:700}}>
                  {t('Registration result')}
                </div>
                {regResults.map((r,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',marginBottom:6,
                    background: r.status==='received'?'rgba(22,163,74,0.08)':r.status==='duplicate'?'rgba(217,119,6,0.08)':'var(--bg-secondary)',
                    border: `1px solid ${r.status==='received'?'rgba(22,163,74,0.22)':r.status==='duplicate'?'rgba(217,119,6,0.20)':'var(--border)'}`,
                    borderRadius:8}}>
                    <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12,flex:1,color:'var(--text-primary)'}}>{r.code}</span>
                    <span style={{fontSize:11,fontWeight:600,color:r.status==='received'?'var(--accent)':r.status==='duplicate'?'var(--color-warning)':r.status==='claimed'?'var(--color-danger)':'var(--text-muted)'}}>
                      {r.status==='received'?t('✅ Already in warehouse'):r.status==='duplicate'?t('⚠ Already registered'):r.status==='claimed'?t('❌ Claimed by another customer'):t('⏳ Waiting')}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Refresh button + list */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1,fontWeight:700}}>
                {t('Registered codes ({n})', { n: expectedList.length })}
              </div>
              <button onClick={handleSync} disabled={syncing||expectedList.filter(e=>e.status==='pending').length===0}
                style={{background:'rgba(22,163,74,0.10)',border:'1px solid rgba(22,163,74,0.25)',borderRadius:6,padding:'5px 12px',fontSize:11,fontWeight:600,color:'var(--accent)',cursor:'pointer',opacity:syncing?0.5:1}}>
                {syncing ? t('⏳ Updating...') : t('🔄 Update status')}
              </button>
            </div>
            {syncResult && (
              <div style={{padding:'8px 12px',marginBottom:10,background:'rgba(22,163,74,0.08)',border:'1px solid rgba(22,163,74,0.22)',borderRadius:8,fontSize:12,color:'var(--accent)'}}>
                {t('✅ Updated — {updated}/{total} codes arrived', { updated: syncResult.updated, total: syncResult.total })}
              </div>
            )}
            {expectedLoading ? (
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:20}}>{t('Loading...')}</div>
            ) : expectedList.length === 0 ? (
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:20}}>{t('No codes registered yet')}</div>
            ) : expectedList.map(e => (
              <div key={e.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',marginBottom:8,
                background: e.status==='received'?'rgba(22,163,74,0.08)':'var(--bg-secondary)',
                border: `1px solid ${e.status==='received'?'rgba(22,163,74,0.22)':'var(--border)'}`,
                borderRadius:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12,fontWeight:700,color:'var(--text-primary)',marginBottom:3}}>{e.code}</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontSize:10,color:'var(--text-muted)'}}>{warehouseName(e.warehouse)}</span>
                    {e.tracking_code && <span style={{fontSize:10,color:'var(--text-muted)'}}>· {e.tracking_code}</span>}
                    {e.scanned_at && <span style={{fontSize:10,color:'var(--text-muted)'}}>· {new Date(e.scanned_at).toLocaleDateString('vi-VN')}</span>}
                  </div>
                </div>
                <span style={{fontSize:11,fontWeight:600,flexShrink:0,
                  color:e.status==='received'?'var(--accent)':'var(--text-muted)'}}>
                  {e.status==='received'?t('✅ Arrived'):t('⏳ Waiting scan')}
                </span>
                <button onClick={()=>handleDeleteExpected(e.id)}
                  style={{background:'transparent',border:'1px solid rgba(220,38,38,0.20)',color:'#b91c1c',borderRadius:5,padding:'3px 8px',fontSize:11,cursor:'pointer'}}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* SHIPMENTS TAB */}
        {activeTab === 'shipments' && (<>
          {bucket && (
            <div style={{marginBottom:12}}>
              <button onClick={()=>setBucket(null)}
                style={{background:'var(--border)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:20,padding:'4px 12px',fontSize:11,color:'var(--text-secondary)',cursor:'pointer'}}>
                {t('✕ Clear filter')}
              </button>
            </div>
          )}

          {loading ? (
            <div style={s.empty}>{t('Loading...')}</div>
          ) : shipments.length === 0 ? (
            <div style={s.empty}>{t('No shipments')}</div>
          ) : (
            <>
              {/* 4 bucket toggle */}
              <BucketButton
                color="#0284c7" icon="📦"
                label={t('At main warehouse')}
                sublabel={t('at main warehouse, will fly soon')}
                count={atMainWarehouse.length}
                active={bucket==='at-main'}
                onClick={()=>toggleBucket('at-main')}
              />
              <BucketButton
                color="#bb86fc" icon="⇄"
                label={t('Awaiting transfer')}
                sublabel={t('at Kho 2 / Kho 3, pending transfer to main warehouse')}
                count={awaitingTransfer.length}
                active={bucket==='transfer'}
                onClick={()=>toggleBucket('transfer')}
              />
              <BucketButton
                color="#4ade80" icon="✈"
                label={t('Flown to Vietnam')}
                sublabel={t('pick a flight to see the shipment list')}
                count={onFlightFlown.length}
                active={bucket==='flown'}
                onClick={()=>toggleBucket('flown')}
              />

              {/* Flight typeahead for "flown" bucket — render ngay dưới badge để khách thấy mối liên hệ */}
              {bucket === 'flown' && flownFlightNames.length > 0 && (
                <div ref={flownRef} style={{position:'relative',marginBottom:12,maxWidth:380}}>
                  <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                    <input className="fi"
                      placeholder={t('Pick a flown flight (click or type to search)...')}
                      value={flownFlight}
                      onChange={e=>{ setFlownFlight(e.target.value); setFlownOpen(true); }}
                      onFocus={()=>setFlownOpen(true)}
                      style={{paddingRight: flownFlight ? 32 : 12}}
                    />
                    {flownFlight && (
                      <button onMouseDown={()=>{ setFlownFlight(''); setFlownOpen(false); }}
                        style={{position:'absolute',right:8,background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
                    )}
                  </div>
                  {flownFlight && (() => {
                    const flightItems = onFlightFlown.filter(s => s.flight_name === flownFlight);
                    const flightCans = flightItems.reduce((sum,s) => sum + (s.can_count||0), 0);
                    return (
                      <div style={{display:'flex',gap:8,marginTop:6,flexWrap:'wrap'}}>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--accent)',background:'var(--accent-bg)',border:'1px solid rgba(22,163,74,0.22)',padding:'3px 10px',borderRadius:20}}>
                          {t('{n} shipments', { n: flightItems.length })}
                        </span>
                        {flightCans > 0 && (
                          <span style={{fontSize:11,fontWeight:700,color:'var(--color-purple)',background:'rgba(124,58,237,0.08)',border:'1px solid rgba(124,58,237,0.18)',padding:'3px 10px',borderRadius:20}}>
                            {t('{n} lon', { n: flightCans })}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {flownOpen && (() => {
                    const list = flownFlight
                      ? flownFlightNames.filter(n=>n.toLowerCase().includes(flownFlight.toLowerCase()))
                      : flownFlightNames;
                    return list.length > 0 && (
                      <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'var(--bg-secondary)',border:'1px solid rgba(15,23,42,0.10)',borderRadius:8,zIndex:50,maxHeight:220,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,0.5)'}}>
                        {list.map(name=>(
                          <div key={name} onMouseDown={()=>{ setFlownFlight(name); setFlownOpen(false); }}
                            style={{padding:'10px 12px',cursor:'pointer',fontSize:13,color:'var(--text-primary)',borderBottom:'1px solid rgba(15,23,42,0.05)'}}
                            onMouseEnter={e=>e.currentTarget.style.background='var(--border)'}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            ✈ {name}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              <BucketButton
                color="#7c3aed" icon="🔒"
                label={t('Holding at warehouse')}
                sublabel={t('held by your request, will ship later')}
                count={heldAtWarehouse.length}
                active={bucket==='hold'}
                onClick={()=>toggleBucket('hold')}
              />

              {/* Cards for the active bucket */}
              {bucket === 'flown' ? (
                !flownFlight ? null : filtered.length === 0 ? (
                  <div style={s.emptySection}>{t('No shipments on flight {name}', { name: flownFlight })}</div>
                ) : (
                  <div style={s.list}>{filtered.map(sh => <ShipCard key={sh.id} sh={sh} onImageClick={setImageModal} onInsideClick={(id,tr)=>setInsideModal({id,tracking:tr})} />)}</div>
                )
              ) : bucket ? (
                filtered.length === 0
                  ? <div style={s.emptySection}>{t('No shipments')}</div>
                  : (
                    <>
                      <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--accent)',background:'var(--accent-bg)',border:'1px solid rgba(22,163,74,0.22)',padding:'3px 10px',borderRadius:20}}>
                          {t('{n} shipments', { n: filtered.length })}
                        </span>
                        {filteredCans > 0 && (
                          <span style={{fontSize:11,fontWeight:700,color:'var(--color-purple)',background:'rgba(124,58,237,0.08)',border:'1px solid rgba(124,58,237,0.18)',padding:'3px 10px',borderRadius:20}}>
                            {t('{n} lon', { n: filteredCans })}
                          </span>
                        )}
                      </div>
                      <div style={s.list}>{filtered.map(sh => <ShipCard key={sh.id} sh={sh} onImageClick={setImageModal} onInsideClick={(id,tr)=>setInsideModal({id,tracking:tr})} />)}</div>
                    </>
                  )
              ) : null}
            </>
          )}
        </>)}
      </div>

      {insideModal && (
        <InsideImagesModal
          shipmentId={insideModal.id}
          tracking={insideModal.tracking}
          onClose={()=>setInsideModal(null)}
        />
      )}

      {imageModal && (
        <div style={s.overlay} onClick={()=>setImageModal(null)}>
          <div style={s.modalBox} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text-primary)'}}>{t('Label image')}</div>
              <button onClick={()=>setImageModal(null)} style={{background:'none',border:'none',color:'var(--text-secondary)',cursor:'pointer',fontSize:24}}>×</button>
            </div>
            <img src={imageModal} alt="Label" style={{maxWidth:'85vw',maxHeight:'75vh',borderRadius:8,objectFit:'contain',display:'block'}} />
          </div>
        </div>
      )}
    </div>
  );
}

const cs = {
  card: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.3)', marginBottom:0 },
  cardTop: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 12px', borderBottom:'1px solid rgba(15,23,42,0.06)', flexWrap:'wrap', gap:4 },
  tracking: { fontVariantNumeric:"tabular-nums",fontWeight:600, fontWeight:700, fontSize:14, color:'var(--accent)' },
  date: { fontSize:11, color:'var(--text-muted)' },
  cardBody: { display:'flex', alignItems:'stretch' },
  infoGrid: { flex:1, padding:'14px 16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' },
  imageWrap: { width:110, flexShrink:0, background:'rgba(15,23,42,0.10)', borderLeft:'1px solid rgba(15,23,42,0.06)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:10, gap:6 },
  imageLabel: { fontSize:8, fontWeight:700, color:'var(--accent)', letterSpacing:1.5, textTransform:'uppercase', background:'var(--accent-bg)', border:'1px solid rgba(22,163,74,0.22)', padding:'2px 7px', borderRadius:20, whiteSpace:'nowrap' },
  image: { width:80, height:80, objectFit:'cover', borderRadius:8, border:'1.5px solid rgba(22,163,74,0.30)', cursor:'pointer', display:'block' },
  imgPlaceholder: { width:80, height:80, borderRadius:8, border:'1px dashed rgba(15,23,42,0.10)', display:'flex', alignItems:'center', justifyContent:'center' },
};

const s = {
  bg: { minHeight:'100vh', background:'var(--bg-primary)' },
  topbar: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', gap:12, background:'var(--bg-tertiary)', borderBottom:'1px solid rgba(15,23,42,0.07)', backdropFilter:'blur(10px)', position:'sticky', top:0, zIndex:10 },
  logoImg: { height:28, width:'auto', display:'block', flexShrink:0, objectFit:'contain' },
  right: { display:'flex', alignItems:'center', gap:8 },
  codePill: { fontVariantNumeric:"tabular-nums",fontWeight:600, fontWeight:700, color:'var(--accent)', background:'var(--accent-bg)', border:'1px solid rgba(22,163,74,0.25)', padding:'3px 10px', borderRadius:20, fontSize:12 },
  logoutBtn: { background:'transparent', border:'1px solid rgba(15,23,42,0.08)', borderRadius:6, padding:'5px 10px', fontSize:11, color:'var(--text-muted)', cursor:'pointer' },
  main: { maxWidth:720, margin:'0 auto', padding:'20px 12px 48px' },
  header: { marginBottom:16 },
  pageLabel: { fontSize:9, fontWeight:700, color:'var(--text-muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:4 },
  pageTitle: { fontSize:20, fontWeight:600, color:'var(--text-primary)' },
  filterBtn: { background:'var(--bg-tertiary)', color:'var(--text-primary)', border:'1px solid var(--accent-strong)', borderRadius:8, padding:'7px 16px', fontSize:13, fontWeight:600, cursor:'pointer', width:'100%' },
  statBadge: { background:'var(--border)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:20, padding:'4px 12px', fontSize:12, color:'var(--text-muted)' },
  sectionHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 },
  sectionTitle: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1.5, textTransform:'uppercase' },
  sectionCount: { fontSize:12, color:'var(--text-muted)' },
  list: { display:'flex', flexDirection:'column', gap:10 },
  emptySection: { padding:'20px', textAlign:'center', color:'var(--text-muted)', fontSize:13, background:'var(--bg-tertiary)', borderRadius:10, border:'1px dashed rgba(15,23,42,0.06)', marginBottom:12 },
  empty: { display:'flex', alignItems:'center', justifyContent:'center', padding:'64px 20px', color:'var(--text-muted)', fontSize:14 },
  overlay: { position:'fixed', inset:0, background:'rgba(15,23,42,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
  modalBox: { background:'var(--bg-secondary)', border:'1px solid rgba(15,23,42,0.10)', borderRadius:12, padding:16, maxWidth:'92vw' },
};
